import React, { useState, useEffect } from 'react';
import DOMPurify from 'dompurify';

const DocumentationAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  const authHeaders = { 'Authorization': `Bearer ${adminToken}`, 'Content-Type': 'application/json' };

  // Documentation states
  const [documentation, setDocumentation] = useState([]);
  const [editingDoc, setEditingDoc] = useState(null);
  const [newDoc, setNewDoc] = useState({
    section: '',
    title: '',
    content: '',
    order: 0,
    is_active: true
  });

  // Load documentation on mount
  useEffect(() => {
    loadDocumentation();
  }, []);

  const loadDocumentation = async () => {
    try {
      const response = await fetch(`${API}/admin/documentation`, {
        headers: authHeaders
      });

      if (response.ok) {
        const data = await response.json();
        setDocumentation(data);
      }
    } catch (error) {
      console.error('Error loading documentation:', error);
    }
  };

  const handleCreateDoc = async () => {
    if (!newDoc.title.trim() || !newDoc.content.trim() || !newDoc.section.trim()) {
      showNotification('Please fill in section, title and content', 'error');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/documentation`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(newDoc)
      });

      if (response.ok) {
        showNotification('Documentation created successfully', 'success');
        setNewDoc({ section: '', title: '', content: '', order: 0, is_active: true });
        loadDocumentation();
      } else {
        showNotification('Failed to create documentation', 'error');
      }
    } catch (error) {
      console.error('Error creating documentation:', error);
      showNotification('Failed to create documentation', 'error');
    }
  };

  const handleUpdateDoc = async (docId, updates) => {
    try {
      const response = await fetch(`${API}/admin/documentation/${docId}`, {
        method: 'PUT',
        headers: {
          ...authHeaders,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      if (response.ok) {
        showNotification('Documentation updated successfully', 'success');
        setEditingDoc(null);
        loadDocumentation();
      } else {
        showNotification('Failed to update documentation', 'error');
      }
    } catch (error) {
      console.error('Error updating documentation:', error);
      showNotification('Failed to update documentation', 'error');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Are you sure you want to delete this documentation?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/admin/documentation/${docId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('Documentation deleted successfully', 'success');
        loadDocumentation();
      } else {
        showNotification('Failed to delete documentation', 'error');
      }
    } catch (error) {
      console.error('Error deleting documentation:', error);
      showNotification('Failed to delete documentation', 'error');
    }
  };

  const handleToggleDocActive = async (docId, currentStatus) => {
    await handleUpdateDoc(docId, { is_active: !currentStatus });
  };

  return (
    <div className="space-y-6">
      {/* Add New Documentation */}
      <div className={`border rounded-lg p-6 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ➕ Add New Documentation
        </h3>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Section
              </label>
              <input
                type="text"
                value={newDoc.section}
                onChange={(e) => setNewDoc({...newDoc, section: e.target.value})}
                placeholder="e.g., features, pricing, about"
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
              />
            </div>
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Title
              </label>
              <input
                type="text"
                value={newDoc.title}
                onChange={(e) => setNewDoc({...newDoc, title: e.target.value})}
                placeholder="e.g., 🌟 About Darija Translator"
                className={`w-full px-3 py-2 rounded-lg border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
              />
            </div>
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Content
            </label>
            {/* WYSIWYG Toolbar */}
            <div className={`flex flex-wrap gap-1 p-2 rounded-t-lg border border-b-0 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-300'}`}>
              <button
                type="button"
                onClick={() => document.execCommand('bold', false, null)}
                className={`px-2 py-1 rounded text-sm font-bold ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                title="Bold"
              >B</button>
              <button
                type="button"
                onClick={() => document.execCommand('italic', false, null)}
                className={`px-2 py-1 rounded text-sm italic ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                title="Italic"
              >I</button>
              <button
                type="button"
                onClick={() => document.execCommand('underline', false, null)}
                className={`px-2 py-1 rounded text-sm underline ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                title="Underline"
              >U</button>
              <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
              <button
                type="button"
                onClick={() => document.execCommand('insertUnorderedList', false, null)}
                className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                title="Bullet List"
              >• List</button>
              <button
                type="button"
                onClick={() => document.execCommand('insertOrderedList', false, null)}
                className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                title="Numbered List"
              >1. List</button>
              <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
              <input
                type="color"
                onChange={(e) => document.execCommand('foreColor', false, e.target.value)}
                className="w-6 h-6 rounded cursor-pointer border-0"
                title="Text Color"
              />
              <button
                type="button"
                onClick={() => {
                  const url = prompt('Enter link URL:');
                  if (url) document.execCommand('createLink', false, url);
                }}
                className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`}
                title="Insert Link"
              >🔗</button>
            </div>
            <div
              contentEditable={true}
              id="new-doc-content"
              onInput={(e) => { const html = e.currentTarget?.innerHTML ?? ''; setNewDoc({...newDoc, content: html}); }}
              className={`w-full min-h-[150px] px-3 py-2 rounded-b-lg border border-t-0 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300 bg-white'
              }`}
              style={{ lineHeight: '1.6' }}
            />
          </div>
          <div className="flex gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Display Order
              </label>
              <input
                type="number"
                value={newDoc.order}
                onChange={(e) => setNewDoc({...newDoc, order: parseInt(e.target.value) || 0})}
                className={`w-20 px-3 py-2 rounded-lg border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newDoc.is_active}
                  onChange={(e) => setNewDoc({...newDoc, is_active: e.target.checked})}
                  className="rounded"
                />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Active
                </span>
              </label>
            </div>
          </div>
          <button
            onClick={handleCreateDoc}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
          >
            Create Documentation
          </button>
        </div>
      </div>

      {/* Documentation List */}
      <div className="space-y-3">
        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          📚 All Documentation ({documentation.length})
        </h3>

        {documentation.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p>No documentation yet. Create your first documentation above.</p>
          </div>
        ) : (
          documentation.map((doc) => (
            <div key={doc.id} className={`border rounded-lg p-4 ${
              isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-white'
            } ${!doc.is_active ? 'opacity-60' : ''}`}>
              {editingDoc === doc.id ? (
                // Edit Mode - WYSIWYG Editor
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <input
                      type="text"
                      defaultValue={doc.section}
                      id={`doc-section-${doc.id}`}
                      placeholder="Section"
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                      }`}
                    />
                    <input
                      type="text"
                      defaultValue={doc.title}
                      id={`doc-title-${doc.id}`}
                      placeholder="Title"
                      className={`w-full px-3 py-2 rounded-lg border ${
                        isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                      }`}
                    />
                  </div>
                  {/* WYSIWYG Toolbar */}
                  <div className={`flex flex-wrap gap-1 p-2 rounded-t-lg border border-b-0 ${isDarkMode ? 'bg-gray-600 border-gray-500' : 'bg-gray-100 border-gray-300'}`}>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => document.execCommand('bold', false, null)} className={`px-2 py-1 rounded text-sm font-bold ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`} title="Bold">B</button>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => document.execCommand('italic', false, null)} className={`px-2 py-1 rounded text-sm italic ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`} title="Italic">I</button>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => document.execCommand('underline', false, null)} className={`px-2 py-1 rounded text-sm underline ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`} title="Underline">U</button>
                    <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => document.execCommand('insertUnorderedList', false, null)} className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`} title="Bullet List">• List</button>
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => document.execCommand('insertOrderedList', false, null)} className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`} title="Numbered List">1.</button>
                    <span className={`mx-1 self-center ${isDarkMode ? 'text-gray-400' : 'text-gray-300'}`}>|</span>
                    <input type="color" onChange={(e) => document.execCommand('foreColor', false, e.target.value)} className="w-6 h-6 rounded cursor-pointer border-0" title="Text Color" />
                    <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => { const url = prompt('Enter link URL:'); if (url) document.execCommand('createLink', false, url); }} className={`px-2 py-1 rounded text-sm ${isDarkMode ? 'bg-gray-500 hover:bg-gray-400 text-white' : 'bg-white hover:bg-gray-200 border'}`} title="Insert Link">🔗</button>
                  </div>
                  <div
                    contentEditable={true}
                    id={`doc-content-${doc.id}`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.content) }}
                    className={`w-full min-h-[200px] px-3 py-2 rounded-b-lg border border-t-0 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                      isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300 bg-white'
                    }`}
                    style={{ lineHeight: '1.6' }}
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        const section = document.getElementById(`doc-section-${doc.id}`).value;
                        const title = document.getElementById(`doc-title-${doc.id}`).value;
                        const content = document.getElementById(`doc-content-${doc.id}`).innerHTML;
                        handleUpdateDoc(doc.id, { section, title, content });
                      }}
                      className="text-sm bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 font-medium"
                    >
                      💾 Save Changes
                    </button>
                    <button
                      onClick={() => setEditingDoc(null)}
                      className={`text-sm px-4 py-2 rounded-lg font-medium ${isDarkMode ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-200 hover:bg-gray-300'}`}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium mr-2 ${
                        isDarkMode ? 'bg-purple-900 text-purple-300' : 'bg-purple-100 text-purple-800'
                      }`}>
                        Section: {doc.section}
                      </span>
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium mr-2 ${
                        isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                      }`}>
                        Order: {doc.order}
                      </span>
                      {!doc.is_active && (
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                        }`}>
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingDoc(doc.id)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleToggleDocActive(doc.id, doc.is_active)}
                        className={doc.is_active ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                      >
                        {doc.is_active ? '👁️ Hide' : '👁️ Show'}
                      </button>
                      <button
                        onClick={() => handleDeleteDoc(doc.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                  <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {doc.title}
                  </h4>
                  <div
                    className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'} line-clamp-3`}
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(doc.content.substring(0, 200) + (doc.content.length > 200 ? '...' : '')) }}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default DocumentationAdmin;
